import { useEffect, useState } from "react";
import { Phone, Mail, MapPin, Clock, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "./Reveal";
import { api, dettaglioErrore } from "@/lib/api";
import {
  TELEFONO,
  TELEFONO_HREF,
  WHATSAPP,
  EMAIL,
  INDIRIZZO,
  SERVIZI_SELECT,
  CITTA,
} from "@/data/citta";

const VUOTO = { nome: "", telefono: "", servizio: "", citta: "", messaggio: "" };

const inputCls =
  "w-full rounded-xl border border-slate-700 bg-obsidian/60 px-4 py-3.5 text-sm text-white placeholder:text-slate-500 outline-none transition-colors focus:border-amber-500";

export function Contatti() {
  const [form, setForm] = useState(VUOTO);
  const [invio, setInvio] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      setForm((f) => ({
        ...f,
        servizio: e.detail.servizio || f.servizio,
        messaggio: f.messaggio || `Ho bisogno di: ${e.detail.voce}`,
      }));
    };
    window.addEventListener("precompila-servizio", handler);
    return () => window.removeEventListener("precompila-servizio", handler);
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const invia = async (e) => {
    e.preventDefault();
    setInvio(true);
    try {
      await api.post("/richieste", form);
      toast.success("Richiesta inviata con successo!", {
        description: "Un operatore ti ricontatterà entro pochi minuti.",
      });
      setForm(VUOTO);
    } catch (err) {
      toast.error("Invio non riuscito", { description: dettaglioErrore(err) });
    } finally {
      setInvio(false);
    }
  };

  return (
    <section id="contatti" className="mx-auto max-w-7xl px-5 py-24 md:px-8 md:py-32">
      <Reveal className="text-center">
        <p className="overline-label">Contatti</p>
        <h2 className="mx-auto mt-4 max-w-2xl font-display text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
          Richiedi assistenza, <span className="text-amber-400">ora.</span>
        </h2>
      </Reveal>

      <div className="mt-14 grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-16">
        <Reveal delay={0.1}>
          <div className="flex h-full flex-col justify-between gap-10 text-center">
            <div className="space-y-7">
              <a href={TELEFONO_HREF} data-testid="contatto-telefono" className="group block">
                <p className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500">
                  Numero di emergenza
                </p>
                <p className="mt-2 flex items-center justify-center gap-3 font-display text-3xl font-extrabold text-amber-400 transition-colors group-hover:text-amber-300 sm:text-4xl">
                  <Phone size={28} strokeWidth={2.2} />
                  {TELEFONO}
                </p>
              </a>
              <div className="space-y-4 text-sm text-slate-300">
                <p className="flex items-center justify-center gap-3">
                  <Phone size={16} className="text-amber-400" />
                  WhatsApp: <span className="text-white">{WHATSAPP}</span>
                </p>
                <p className="flex items-center justify-center gap-3" data-testid="contatto-email">
                  <Mail size={16} className="text-amber-400" />
                  {EMAIL}
                </p>
                <p className="flex items-center justify-center gap-3" data-testid="contatto-indirizzo">
                  <MapPin size={16} className="text-amber-400" />
                  {INDIRIZZO}
                </p>
                <p className="flex items-center justify-center gap-3">
                  <Clock size={16} className="text-amber-400" />
                  Sempre aperti · 24 ore su 24, 7 giorni su 7
                </p>
              </div>
            </div>
            <div className="rounded-2xl border border-emerald-500/25 bg-emerald-500/5 p-6">
              <p className="text-sm leading-relaxed text-slate-300">
                <span className="font-semibold text-emerald-400">Emergenza in corso?</span>{" "}
                Non compilare il modulo: chiama subito il numero di emergenza. Un operatore
                attiva il tecnico più vicino mentre sei ancora in linea.
              </p>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.2}>
          <form
            onSubmit={invia}
            data-testid="form-richiesta"
            className="glass-panel rounded-2xl p-7 md:p-9"
          >
            <h3 className="text-center font-display text-xl font-semibold text-white">Lascia un messaggio</h3>
            <p className="mt-1.5 text-center text-sm text-slate-400">
              Ti richiamiamo noi entro pochi minuti, senza impegno.
            </p>
            <div className="mt-7 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="form-nome" className="mb-1.5 block text-xs font-medium text-slate-400">
                  Nome e cognome *
                </label>
                <input
                  id="form-nome"
                  data-testid="form-richiesta-nome"
                  required
                  value={form.nome}
                  onChange={set("nome")}
                  placeholder="Mario Rossi"
                  className={inputCls}
                />
              </div>
              <div>
                <label htmlFor="form-telefono" className="mb-1.5 block text-xs font-medium text-slate-400">
                  Telefono *
                </label>
                <input
                  id="form-telefono"
                  data-testid="form-richiesta-telefono"
                  required
                  type="tel"
                  value={form.telefono}
                  onChange={set("telefono")}
                  placeholder="333 123 4567"
                  className={inputCls}
                />
              </div>
              <div>
                <label htmlFor="form-servizio" className="mb-1.5 block text-xs font-medium text-slate-400">
                  Tipo di intervento *
                </label>
                <select
                  id="form-servizio"
                  data-testid="form-richiesta-servizio"
                  required
                  value={form.servizio}
                  onChange={set("servizio")}
                  className={inputCls}
                >
                  <option value="" disabled>
                    Seleziona un servizio
                  </option>
                  {SERVIZI_SELECT.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="form-citta" className="mb-1.5 block text-xs font-medium text-slate-400">
                  Città *
                </label>
                <select
                  id="form-citta"
                  data-testid="form-richiesta-citta"
                  required
                  value={form.citta}
                  onChange={set("citta")}
                  className={inputCls}
                >
                  <option value="" disabled>
                    Seleziona la città
                  </option>
                  {CITTA.map((c) => (
                    <option key={c.slug} value={c.nome}>
                      {c.nome}
                    </option>
                  ))}
                  <option value="Altra città">Altra città in Lombardia</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="form-messaggio" className="mb-1.5 block text-xs font-medium text-slate-400">
                  Descrivi il problema
                </label>
                <textarea
                  id="form-messaggio"
                  data-testid="form-richiesta-messaggio"
                  rows={4}
                  value={form.messaggio}
                  onChange={set("messaggio")}
                  placeholder="Es. perdita d'acqua dal sifone del lavandino in cucina…"
                  className={`${inputCls} resize-none`}
                />
              </div>
            </div>
            <button
              type="submit"
              data-testid="form-richiesta-submit-btn"
              disabled={invio}
              className="mt-7 inline-flex w-full items-center justify-center gap-2.5 rounded-full bg-amber-500 px-8 py-4 text-base font-bold text-obsidian transition-all hover:bg-amber-400 hover:shadow-[0_12px_40px_rgba(245,158,11,0.3)] disabled:opacity-60"
            >
              {invio ? <Loader2 size={19} className="animate-spin" /> : <Send size={18} />}
              {invio ? "Invio in corso…" : "Richiedi assistenza"}
            </button>
            <p className="mt-4 text-center text-xs text-slate-500">
              Inviando accetti di essere ricontattato dal nostro team. Nessuno spam, mai.
            </p>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
